import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { setupLookup } from '../auth/authApi.ts';
import { completeSetup, useAuth } from '../lib/auth.ts';
import type { AuthState } from '../lib/auth.ts';
import { ApiError, asSentence } from '../lib/http.ts';
import { replaceHash } from '../lib/router.ts';
import { AuthCard } from '../components/AuthCard.tsx';
import {
  NewPasswordFields,
  newPasswordFault,
  newPasswordFieldId,
} from '../components/NewPasswordFields.tsx';
import type { NewPasswordFault } from '../components/NewPasswordFields.tsx';

const ID_PREFIX = 'setup';
const FOOTNOTE =
  'A setup link works once, and for 72 hours. If this one no longer works, ask the admin for a new link.';

/** What the token lookup found. `invalid` is the server's 400; `failed` is anything else. */
export type SetupLookup =
  | { status: 'loading' }
  | { status: 'found'; email: string }
  | { status: 'invalid'; message: string }
  | { status: 'failed'; message: string };

export type SetupScreen = 'wait' | 'form' | 'invalid' | 'retry' | 'leave';

/**
 * Which screen the setup route shows. A dead link never shows a form that cannot succeed. A person
 * who is already signed in has no use for the dead-link screen either, so they go home — but only
 * once `me` has answered, because "signed in" is not known before that.
 */
export function setupScreen(lookup: SetupLookup, auth: AuthState['status']): SetupScreen {
  if (lookup.status === 'loading') return 'wait';
  if (lookup.status === 'found') return 'form';
  if (lookup.status === 'failed') return 'retry';
  if (auth === 'signed_in') return 'leave';
  return auth === 'loading' ? 'wait' : 'invalid';
}

/**
 * `#/setup/:token` — the one-time link an admin sends by hand (docs/specs/accounts.md, section 4).
 * The page first asks whose link it is, which does not use the token up. Then the person sets a
 * password, and the server logs them in with the same answer.
 */
export function SetupView({ token }: { token: string }): JSX.Element | null {
  const auth = useAuth();
  const [lookup, setLookup] = useState<SetupLookup>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLookup({ status: 'loading' });
    setupLookup(token)
      .then(({ email }) => !cancelled && setLookup({ status: 'found', email }))
      .catch((e: unknown) => {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : String(e);
        const invalid = e instanceof ApiError && e.status === 400;
        setLookup({ status: invalid ? 'invalid' : 'failed', message });
      });
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  const screen = setupScreen(lookup, auth.status);

  useEffect(() => {
    // `replaceHash`, so that the dead token does not stay in the history either.
    if (screen === 'leave') replaceHash('#/');
  }, [screen]);

  if (screen === 'leave') return null;
  if (screen === 'wait') {
    return (
      <AuthCard>
        <span className="label auth-wait">loading…</span>
      </AuthCard>
    );
  }
  if (lookup.status === 'invalid') {
    return (
      <AuthCard title="This link does not work" footnote={FOOTNOTE}>
        <p className="notice err" role="alert">
          {asSentence(lookup.message)}
        </p>
        <button className="btn block" type="button" onClick={() => replaceHash('#/login')}>
          Go to sign in
        </button>
      </AuthCard>
    );
  }
  if (lookup.status === 'failed') {
    return (
      <AuthCard title="Cannot check this link">
        <p className="notice err" role="alert">
          {asSentence(lookup.message)}
        </p>
        <button className="btn block" type="button" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </button>
      </AuthCard>
    );
  }
  if (lookup.status !== 'found') return null;
  return <SetupForm token={token} email={lookup.email} />;
}

/** The form, once the link is known to be good. Exported so that a test can render it. */
export function SetupForm({ token, email }: { token: string; email: string }): JSX.Element {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fault, setFault] = useState<NewPasswordFault | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    const found = newPasswordFault(password, confirm);
    setFault(found);
    setError(null);
    if (found) {
      document.getElementById(newPasswordFieldId(ID_PREFIX, found.field))?.focus();
      return;
    }
    setBusy(true);
    try {
      await completeSetup(token, password);
      // Success leaves this route, so there is no state left to set.
    } catch (e) {
      // The server says which it was: an expired link, a used link, or a refused password.
      setError(asSentence(e instanceof Error ? e.message : String(e)));
      setBusy(false);
    }
  }

  return (
    <AuthCard title="Set your password" account={email} footnote={FOOTNOTE}>
      <form className="form" noValidate onSubmit={(e) => void onSubmit(e)}>
        {/* Not shown, but read: it tells a password manager which login the new password is for. */}
        <input type="email" name="email" autoComplete="username" value={email} readOnly hidden />
        <NewPasswordFields
          idPrefix={ID_PREFIX}
          password={password}
          confirm={confirm}
          fault={fault}
          onPassword={setPassword}
          onConfirm={setConfirm}
        />
        {error && (
          <p className="notice err" role="alert">
            {error}
          </p>
        )}
        <button className="btn primary block" type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Set password and sign in'}
        </button>
      </form>
    </AuthCard>
  );
}
